import { graphql } from 'gatsby';
import React, { useRef } from 'react';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { useHasEnteredViewport } from '../../../utils/hooks';
import bottomCloud from '../../../images/photoGalleryModule_cloud_bottom.webp';
import topCloud from '../../../images/photoGalleryModule_cloud_top.webp';
import { CommonModuleProps } from '../../ModulesContent';
import AnimatedTitle from '../AnimatedTitle';
import { useScrollLinkedClouds } from '../Background';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import ScrollingItems from '../ScrollingItems';
import * as styles from './PhotoGalleryModule.module.scss';

const ELEMENT_ANIMATION_DURATION = 10;

export const PhotoGalleryModuleFragment = graphql`
  fragment PhotoGalleryModule on SanityPhotoGalleryModule {
    title {
      ...LocaleStringFragment
    }
    photos {
      ...SanityImage
    }
  }
`;

export type PhotoGalleryModuleProps = {
  title?: string;
  photos: Array<SanityImageType>;
  className?: string;
};

function PhotoGalleryModule(
  props: PhotoGalleryModuleProps & CommonModuleProps,
): React.ReactElement {
  const { title, photos, className, moduleId } = props;

  const containerRef = useRef<HTMLElement | null>(null);
  const [viewportRef, hasEnteredViewport] = useHasEnteredViewport();

  useScrollLinkedClouds(`.${styles.topCloud}, .${styles.bottomCloud}`, {
    scope: containerRef,
  });

  return (
    <ModuleLayout
      id={moduleId}
      className={className}
      currentModuleBgColor={props.theme}
      childrenWithoutHorizontalPadding
      contentClassName={styles.contentContainer}
      ref={containerRef}
    >
      {title && (
        <div className={styles.titleContainer}>
          <AnimatedTitle title={title} trigger={styles.contentContainer} className={styles.title} />
        </div>
      )}
      <div className={styles.scrollingContainer}>
        <img src={topCloud} className={styles.topCloud} alt="" loading="lazy" />
        <img src={bottomCloud} className={styles.bottomCloud} alt="" loading="lazy" />
        <div className={styles.galleryContainer} ref={viewportRef}>
          <ScrollingItems
            animationDuration={ELEMENT_ANIMATION_DURATION * photos.length + 1}
            className={styles.scrollingWrapper}
          >
            <div className={styles.gallery}>
              {photos.map((photo, i) => (
                <div className={styles.logoContainer} key={i}>
                  <Image
                    className={styles.logo}
                    image={photo}
                    width={1050}
                    sizes="(min-width: 2100px) 700px, 450px"
                    eagerLoading={hasEnteredViewport}
                  />
                </div>
              ))}
            </div>
          </ScrollingItems>
        </div>
      </div>
    </ModuleLayout>
  );
}

export default PhotoGalleryModule;
